import {
  DetailsList,
  DetailsListLayoutMode,
  DetailsHeader,
  DetailsRow,
  IColumn,
  IDetailsHeaderProps,
  IDetailsHeaderStyles,
  IDetailsRowProps,
  IDetailsRowStyles,
  IDetailsColumnRenderTooltipProps,
  IRenderFunction,
  SelectionMode,
  Text,
  TooltipHost,
} from "@fluentui/react";
import { CSSProperties, useCallback, useMemo, useState } from "react";
import fluentUiClasses, { fluentUiBoldStyle, fluentUiDarkTheme, fluentUiGridStyles } from "system/styles/fluent-ui-classes";
import LoadingPage from "../system/LoadingPage";
import { IPackageInfo } from "system/types/interfaces/IPackageInfo";
import { getSorter } from "system/libraries/utilities";

interface PackageListProps {
  items: IPackageInfo[];
  isLoading: boolean;
  showSourceColumn?: boolean;
}

const DEFAULT_ICON = "/img/DefaultPackage.png";

const PackageList = ({ items, isLoading, showSourceColumn = true }: PackageListProps) => {
  const [sortedColumnField, setSortedColumnField] = useState<string>("name");
  const [isSortedDescending, setIsSortedDescending] = useState<boolean>(false);

  const onColumnClickHandler = useCallback(
    (_: unknown, column: IColumn) => {
      if (sortedColumnField === column.fieldName) {
        setIsSortedDescending((prev) => !prev);
        return;
      }
      setSortedColumnField(column.fieldName ?? sortedColumnField);
      setIsSortedDescending(false);
    },
    [sortedColumnField]
  );

  const sortedItems = useMemo<IPackageInfo[]>(() => {
    return [...items].sort(getSorter(sortedColumnField, isSortedDescending));
  }, [items, sortedColumnField, isSortedDescending]);

  const columns = useMemo<IColumn[]>(() => {
    const cols: IColumn[] = [
      {
        key: "icon",
        name: "Icon",
        className: fluentUiClasses.iconCell,
        iconClassName: fluentUiClasses.iconHeader,
        ariaLabel: "Package icon",
        iconName: "Page",
        isIconOnly: true,
        fieldName: "iconUrl",
        minWidth: 32,
        maxWidth: 32,
        onRender: (item: IPackageInfo) => (
          <TooltipHost content="Package">
            <img
              src={item.iconUrl || DEFAULT_ICON}
              alt={item.name}
              style={{ width: 32, height: 32, objectFit: "contain" }}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = DEFAULT_ICON;
              }}
            />
          </TooltipHost>
        ),
      },
      {
        key: "name",
        name: "Package Name",
        fieldName: "name",
        minWidth: 300,
        maxWidth: 600,
        isRowHeader: true,
        isResizable: true,
        isSorted: sortedColumnField === "name",
        isSortedDescending: isSortedDescending && sortedColumnField === "name",
        sortAscendingAriaLabel: "Sorted A to Z",
        sortDescendingAriaLabel: "Sorted Z to A",
        onColumnClick: onColumnClickHandler,
        data: "string",
        isPadded: true,
      },
      {
        key: "description",
        name: "Description",
        fieldName: "description",
        minWidth: 300,
        maxWidth: 600,
        isResizable: true,
        isMultiline: true,
        onColumnClick: onColumnClickHandler,
        data: "string",
        isPadded: true,
        onRender: (item: IPackageInfo) => (
          <span style={{ color: "rgba(255, 255, 255, 0.75)", fontSize: "13px" }}>
            {item.description || ""}
          </span>
        ),
      },
    ];

    if (showSourceColumn) {
      cols.push({
        key: "source",
        name: "Source",
        fieldName: "customerShortname",
        minWidth: 220,
        maxWidth: 420,
        isResizable: true,
        isSorted: sortedColumnField === "customerShortname",
        isSortedDescending: isSortedDescending && sortedColumnField === "customerShortname",
        onColumnClick: onColumnClickHandler,
        data: "string",
        isPadded: true,
        onRender: (item: IPackageInfo) => {
          const label = item.isCustomerSpecific ? item.customerShortname ?? "Specific" : "Global";
          const isGlobal = !item.isCustomerSpecific;
          return (
            <span
              style={{
                color: isGlobal ? "#0078D4" : "var(--text-color, #FFFFFF)",
                border: `1px solid ${isGlobal ? "#0078D4" : "rgba(255, 255, 255, 0.3)"}`,
                padding: "2px 10px",
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: 500,
                backgroundColor: "transparent",
                letterSpacing: "0.3px",
              }}
            >
              {label}
            </span>
          );
        },
      });
    }

    return cols;
  }, [showSourceColumn, sortedColumnField, isSortedDescending, onColumnClickHandler]);

  const onRenderColumnHeaderTooltipHandler = useCallback((props?: IDetailsColumnRenderTooltipProps) => {
    const customStyles: CSSProperties = {
      color: fluentUiDarkTheme.palette.black,
      backgroundColor: fluentUiDarkTheme.palette.blackTranslucent40,
    };
    if (props) {
      return (
        <span className={props.hostClassName} style={customStyles}>
          {props.children}
        </span>
      );
    }
    return null;
  }, []);

  const onRenderDetailsHeaderHandler = useCallback<IRenderFunction<IDetailsHeaderProps>>(
    (props?: IDetailsHeaderProps) => {
      const customStyles: Partial<IDetailsHeaderStyles> = {};
      if (props) {
        customStyles.root = {
          backgroundColor: fluentUiDarkTheme.palette.blackTranslucent40,
          color: fluentUiDarkTheme.palette.black,
        };
        return (
          <DetailsHeader
            {...props}
            styles={customStyles}
            theme={fluentUiDarkTheme}
            onRenderColumnHeaderTooltip={onRenderColumnHeaderTooltipHandler}
          />
        );
      }
      return null;
    },
    [onRenderColumnHeaderTooltipHandler]
  );

  const onRenderRowHandler = useCallback((props?: IDetailsRowProps) => {
    const customStyles: Partial<IDetailsRowStyles> = {};
    if (props) {
      customStyles.root = {
        backgroundColor: fluentUiDarkTheme.palette.blackTranslucent40,
      };
      customStyles.isRowHeader = { ...customStyles.root };
      return <DetailsRow {...props} styles={customStyles} theme={fluentUiDarkTheme} />;
    }
    return null;
  }, []);

  const getItemKey = useCallback(
    (item: IPackageInfo, index?: number) => `${item.name}-${item.customerShortname ?? "global"}-${index ?? 0}`,
    []
  );

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <>
      <DetailsList
        items={sortedItems}
        compact={false}
        columns={columns}
        selectionMode={SelectionMode.none}
        getKey={getItemKey}
        setKey="none"
        layoutMode={DetailsListLayoutMode.justified}
        isHeaderVisible={true}
        styles={fluentUiGridStyles}
        onRenderDetailsHeader={onRenderDetailsHeaderHandler}
        onRenderRow={onRenderRowHandler}
        theme={fluentUiDarkTheme}
      />
      <div className={fluentUiClasses.controlWrapper} style={{ marginTop: "10px" }}>
        <Text variant="large" styles={fluentUiBoldStyle}>
          {`Packages Count: ${sortedItems.length}`}
        </Text>
      </div>
    </>
  );
};

export default PackageList;
